import { type Event, isInternalEvent } from './events';
import type { WebinarTimeZone } from './webinar/types';

// TODO: fix this once we figure out the date format
export function eventInPast(event: Event, now: Date = new Date()) {
  const eventDate = new Date(event.timeStamp);
  const today = new Date(now);

  // Reset time to allow for comparison of dates only
  // this has the effect of allowing events "today" to be shown as upcoming
  eventDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  return eventDate < today;
}

export function groupEventsByDate(events: Event[]) {
  // Sort all events first (ascending order)
  const sortedEvents = [...events].sort((a, b) => new Date(a.timeStamp).getTime() - new Date(b.timeStamp).getTime());

  const pastEvents: Event[] = [];
  const upcomingEvents: Event[] = [];

  for (const event of sortedEvents) {
    if (eventInPast(event)) {
      pastEvents.push(event);
    } else {
      upcomingEvents.push(event);
    }
  }

  // most recent first
  pastEvents.reverse();

  return { pastEvents, upcomingEvents };
}

export function calculateLocalTime(timeStamp: string) {
  const dateObj = new Date(timeStamp);
  const localDate = dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: '2-digit',
  });
  const localTime = dateObj.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return { localDate, localTime };
}

// function to turn 24 hour time string into 12 hour time string and remove PST etc.
// e.g. 14:00 PST becomes 2:00 PM
export const convertTo12HourFormat = (time: string) => {
  // remove PST or any other timezone information

  const [hour, minute] = time.split(/[: ]/); // split into hour and minute and timezone (but disregard timezone)
  const hour12 = parseInt(hour, 10) % 12 || 12; // convert to 12 hour format
  const ampm = parseInt(hour, 10) < 12 ? 'AM' : 'PM'; // determine AM/PM

  return `${hour12}:${minute} ${ampm}`;
};

// Whether an event may appear on the events listing, sitemap, JSON-LD index and past-event
// grids. `unlistedUntilRecording` events are direct-link only until they're past and have a
// recording, at which point they list as on-demand like any past event.
export function isListedEvent(event: Event, now: Date = new Date()) {
  if (!isInternalEvent(event) || !event.unlistedUntilRecording) return true;
  return eventInPast(event, now) && Boolean(event.videoLink);
}

// e.g. 'Wed, November 4, 2026' — the date in the event's first listed time zone.
export function formatWebinarDate(timeStamp: string, timeZone: string) {
  return new Date(timeStamp).toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone,
  });
}

// e.g. ['8:00 AM PT', '11:00 AM ET', '5:00 PM CET'].
export function formatWebinarTimes(timeStamp: string, timeZones: readonly WebinarTimeZone[]) {
  const date = new Date(timeStamp);
  return timeZones.map(({ timeZone, label }) => {
    const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone });
    return `${time} ${label}`;
  });
}
